import { debounceTime, mergeMap, map, filter, take, catchError } from 'rxjs/operators';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, EMPTY, timer } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

import {
  Product,
  SearchService,
  Autocomplete,
  ProductionService,
  SRouterLinkWithHrefDirective,
  TranslatePipe,
} from '@speedy/core';

import { AutocompleteCategoryComponent } from './autocomplete-category/autocomplete-category.component';
import { AutocompleteTermsComponent } from './autocomplete-terms/autocomplete-terms.component';
import { AutocompleteProductSingleComponent } from './autocomplete-product-single/autocomplete-product-single.component';
import { AutocompleteProductsListComponent } from './autocomplete-products-list/autocomplete-products-list.component';

@Component({
  selector: 'app-top-search',
  templateUrl: './top-search.component.html',
  styleUrls: ['./top-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatIcon,
    ReactiveFormsModule,
    AutocompleteProductsListComponent,
    AutocompleteProductSingleComponent,
    AutocompleteTermsComponent,
    AutocompleteCategoryComponent,
    SRouterLinkWithHrefDirective,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class TopSearchComponent implements OnInit {
  private readonly productionService = inject(ProductionService);
  private readonly searchService = inject(SearchService);
  private readonly cdr = inject(ChangeDetectorRef);

  search = new FormControl('');

  autocompleteResult$ = new BehaviorSubject<Autocomplete | null>(null);
  singleProduct$ = new BehaviorSubject<Product | null>(null);

  products$ = this.autocompleteResult$.pipe(map((result) => result?.products));

  searchTerms$ = this.autocompleteResult$.pipe(
    map((result) => (result?.popular_terms ? result.popular_terms : [])),
  );
  categories$ = this.autocompleteResult$.pipe(
    map((result) => (result?.categories ? result.categories : [])),
  );

  isSearchByExactMatch$ = this.search.valueChanges.pipe(
    map((value) => value?.match(this.regexp)),
  );

  loading = false;
  isAutocompleteOpened = false;
  lastNonSearchRoute = '';

  /** checking any text + space + any text */
  regexp = new RegExp(/.+\s.+/);

  ngOnInit() {
    if (this.productionService.isServerMode()) return;

    this.search.valueChanges
      .pipe(
        debounceTime(650),
        filter((query) => {
          if (query && query.length >= 3) {
            return true;
          } else {
            this.autocompleteResult$.next(null);
            return false;
          }
        }),
        mergeMap((query) => this.searchService.search(query as string)),
      )
      .subscribe((result) => {
        this.autocompleteResult$.next(result);
        this.isAutocompleteOpened = !!result;
      });
  }

  clear() {
    this.search.setValue('');
  }

  hoverProductItem(product: Product) {
    timer(100)
      .pipe(take(1))
      .subscribe(() => {
        this.singleProduct$.next(product ? product : null);
        this.cdr.detectChanges();
      });
  }

  toggleAutocomplete() {
    this.isAutocompleteOpened = !this.isAutocompleteOpened;
  }
}
